import React, { useCallback, useContext, useEffect, useState } from 'react'
import ContentEditor from '@mother/content'
import { throttle } from 'lodash'
import { Text } from 'oio-react'
import PropTypes from 'prop-types'
import noteStatuses from 'config/constants/noteStatuses'
import { useUpdateNoteExperimental, useRequestNoteLock } from 'src/graphql/hooks'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'

// The styles used here are primarily done for prototyping convenience.
// These will likely become part of the mother/content repo or
// abstracted to a more logical location in this project
// (but presumeably the V3 and V2 components in notes will also be made more
// generic than used just in notes)
// PR #699
const stylesheet = {
   fontSize: 'inherit',
   lineHeight: '160%',
   color: 'inherit',
   letterSpacing: '0.1px',
   'strong, b': {
      fontWeight: 500
   },
   h1: {
      fontSize: '2.4em',
      lineHeight: '1.1em',
      fontWeight: 500
   },
   a: {
      color: '#888',
      textDecoration: 'underline'
   },
   p: {
      margin: 0
   },
   pre: {
      borderRadius: '3px',
      caretColor: 'red',
      overflowX: 'auto',
      overflowY: 'hidden',
      wordWrap: 'normal',
      fontSize: '0.8em',
      fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
      margin: 0,
      padding: '15px 20px',
      background: 'rgb(41, 45, 62)',
      color: 'rgb(191, 199, 213)',
      lineHeight: 1.4
   },
   ol: {
      margin: '0 0 0 20px',
      padding: 0
   },
   ul: {
      margin: '0 0 0 20px',
      padding: 0,
      listStyle: 'none'
   },
   'ul li:before': {
      // content: '"• "', // alternative
      content: '"- "',
      position: 'absolute',
      top: '0px',
      left: '-18px',
      fontSize: '20px',
      color: 'inherit'
   },
   li: {
      position: 'relative',
      padding: '0px 0 0px 5px',
      margin: 0
   },
   '.mc-checklist': {
      margin: '10px 0',
      borderRadius: '5px',
      li: {
         position: 'relative',
         display: 'flex',
         alignItems: 'center',
         padding: '5px 0px',
         fontSize: '0.9em'
      },
      'li:before': {
         content: '""'
      },
      '.mc-checkbox': {
         position: 'absolute',
         top: '8px',
         left: '3px'
      },
      '.mc-checklistitem-completed': {
         color: '#999',
         '.mc-checkbox-icon': {
            fill: '#aaa'
         }
      }
   }
}

const NoteTextV3 = ({ note, noteStatus }) => {
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const { requestNoteLock } = useRequestNoteLock()
   const { updateNote } = useUpdateNoteExperimental()
   const [noteKey, setNoteKey] = useState(JSON.stringify(note?.content?.elements))

   useEffect(() => {
      setNoteKey(JSON.stringify(note?.content?.elements))
   }, [note])

   const handleContentChanged = useCallback(throttle(async (newContent) => {
      await requestNoteLock({ id: note.id })
      updateNote(
         { id: note.id },
         { content: newContent },
         { fetchPolicy: 'no-cache' }
      )
   }, 3000), [note.id])

   const handleBlurEditor = useCallback((e) => {
      // If Esc key was pressed, blur focus
      if (e.keyCode === 27 && 'activeElement' in document) {
         document.activeElement.blur()
      }
   }, [])

   return (
      <Text
         size={theme.tmpBodyTextSize}
         color={theme.tmpBodyTextColor}
         lineHeight={theme.tmpBodyTextLineHeight}>
         <ContentEditor
            key={noteKey}
            initialValue={note.content.elements}
            onChange={handleContentChanged}
            onKeyDown={handleBlurEditor}
            readOnly={noteStatus === noteStatuses.NOTE_STATUS_LOCKED}
            stylesheet={{
               ...stylesheet,
               '.mc-checklist > li': {
                  borderBottom: `1px solid ${theme.tmpContentLineColor}`
               },
               '.mc-checklist > li:first-child': {
                  borderTop: `1px solid ${theme.tmpContentLineColor}`
               }
            }}
         />
      </Text>
   )
}

NoteTextV3.propTypes = {
   note: PropTypes.object.isRequired,
   noteStatus: PropTypes.number.isRequired
}

export default NoteTextV3
